import cn from 'classnames';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { debug } from '@/shared/lib';
import {
  Button,
  ErrorText,
  FileDropzone,
  FileDropzoneHandle,
  Icon,
  Tabs,
  Textarea,
} from '@/shared/ui';

import type { DocumentTemplateParseResponse } from '@/entities/templates';
import { templatesModel } from '@/entities/templates';

import {
  IMPORT_ERROR_MESSAGES,
  IMPORT_MODE_CONFIG,
  IMPORT_MODES,
  IMPORT_PARSE_MODE_API_MAP,
  templateEditorMessages,
} from '../../config';
import type {
  ImportMode,
  ImportPanelError,
  ImportPendingResult,
  ImportStrategy,
} from '../../config';
import { getImportFileKind, hasParsedSections } from '../../lib';

import styles from './ImportSourcePanel.module.scss';

type ImportSourcePanelProps = {
  className?: string;
  hasExistingSections?: boolean;
  onApplyJson: (content: string, strategy: ImportStrategy) => boolean;
  onParsed: (
    result: DocumentTemplateParseResponse,
    sourceContent: string,
    strategy: ImportStrategy,
  ) => void;
  onCancel?: () => void;
};

export const ImportSourcePanel: FC<ImportSourcePanelProps> = (props) => {
  const {
    className,
    hasExistingSections = false,
    onApplyJson,
    onParsed,
    onCancel,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [mode, setMode] = useState<ImportMode>('file');
  const [content, setContent] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<ImportPanelError | null>(null);
  const [pendingResult, setPendingResult] =
    useState<ImportPendingResult | null>(null);

  const dropzoneRef = useRef<FileDropzoneHandle>(null);
  const parseRequestRef = useRef<{ abort: () => void } | null>(null);

  // A parse finishing after the dialog closed must not reach the form.
  useEffect(() => () => parseRequestRef.current?.abort(), []);

  const handleModeChange = useCallback((nextMode: string) => {
    setMode(nextMode as ImportMode);
    setError(null);
    setPendingResult(null);
  }, []);

  const handleContentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setContent(event.target.value);
      setError(null);
      setPendingResult(null);
    },
    [],
  );

  const applyJson = useCallback(
    (jsonContent: string, strategy: ImportStrategy) => {
      if (onApplyJson(jsonContent, strategy)) {
        return;
      }
      setMode('file');
      setError('invalidJson');
    },
    [onApplyJson],
  );

  const commitResult = useCallback(
    (result: ImportPendingResult, strategy: ImportStrategy) => {
      setPendingResult(null);
      if (result.kind === 'json') {
        applyJson(result.content, strategy);
        return;
      }
      onParsed(result.result, result.sourceContent, strategy);
    },
    [applyJson, onParsed],
  );

  const handleSubmit = useCallback(async () => {
    if (mode === 'file') {
      dropzoneRef.current?.open();
      return;
    }

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      return;
    }

    setIsParsing(true);
    const parseRequest = dispatch(
      templatesModel.parseDocumentTemplateThunk({
        content: trimmedContent,
        parse_mode: IMPORT_PARSE_MODE_API_MAP[mode],
      }),
    );
    parseRequestRef.current = parseRequest;
    try {
      const action = await parseRequest;

      if (templatesModel.parseDocumentTemplateThunk.rejected.match(action)) {
        if (!action.meta.aborted) {
          debug('Failed to parse imported text', action.error);
          setError('parseFailed');
        }
        return;
      }

      const result = action.payload;

      if (!hasParsedSections(result)) {
        setError('noSections');
        return;
      }

      if (hasExistingSections) {
        setPendingResult({
          kind: 'parsed',
          result,
          sourceContent: trimmedContent,
        });
        return;
      }

      onParsed(result, trimmedContent, 'replace');
    } finally {
      parseRequestRef.current = null;
      setIsParsing(false);
    }
  }, [content, dispatch, hasExistingSections, mode, onParsed]);

  const processFile = useCallback(
    async (file: File) => {
      if (getImportFileKind(file) === 'unsupported') {
        setError('unsupportedFile');
        return;
      }

      const fileContent = await file.text();
      if (!fileContent.trim()) {
        setError('emptyFile');
        return;
      }

      if (getImportFileKind(file) === 'json') {
        if (hasExistingSections) {
          setPendingResult({ kind: 'json', content: fileContent });
          return;
        }
        applyJson(fileContent, 'replace');
        return;
      }

      setContent(fileContent);
      setError(null);
      setPendingResult(null);
      if (mode === 'file') {
        setMode('blank_form');
      }
    },
    [applyJson, hasExistingSections, mode],
  );

  const modeConfig = IMPORT_MODE_CONFIG[mode];
  const isFileMode = mode === 'file';

  const uploadArea = (
    <FileDropzone
      ref={dropzoneRef}
      className={styles.dropzone}
      titleClassName={styles.dropzoneTitle}
      descriptionClassName={styles.dropzoneDescription}
      icon={
        <Icon
          name="documentImport"
          size={42}
          className={styles.dropzoneGlyph}
        />
      }
      title={formatMessage(templateEditorMessages.importUploadFile)}
      description={formatMessage(modeConfig.dropHint)}
      accept={modeConfig.accept}
      onFile={(file) => void processFile(file)}
    />
  );

  return (
    <div className={cn(styles.panel, className)}>
      <Tabs.Root value={mode} onValueChange={handleModeChange}>
        <Tabs.List size="small" variant="pill">
          {IMPORT_MODES.map((segmentMode) => (
            <Tabs.Trigger
              key={segmentMode}
              value={segmentMode}
              size="small"
              variant="pill"
            >
              {formatMessage(IMPORT_MODE_CONFIG[segmentMode].label)}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs.Root>

      {isFileMode ? (
        uploadArea
      ) : (
        <div className={styles.textSurface}>
          <Textarea
            value={content}
            rows={7}
            onChange={handleContentChange}
            placeholder={
              modeConfig.placeholder
                ? formatMessage(modeConfig.placeholder)
                : undefined
            }
          />
          {uploadArea}
        </div>
      )}

      {error && (
        <ErrorText error={formatMessage(IMPORT_ERROR_MESSAGES[error])} />
      )}

      {pendingResult ? (
        <div className={cn(styles.actions, styles.actionsWithPrompt)}>
          <span className={cn('p4', styles.strategyPrompt)}>
            {formatMessage(templateEditorMessages.importStrategyPrompt)}
          </span>
          <div className={styles.actionButtons}>
            <Button
              variant="secondary"
              size="medium"
              onClick={() => commitResult(pendingResult, 'append')}
            >
              {formatMessage(templateEditorMessages.importAddToExisting)}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={() => commitResult(pendingResult, 'replace')}
            >
              {formatMessage(templateEditorMessages.importReplaceAll)}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <div className={styles.actionButtons}>
            {onCancel && (
              <Button variant="tertiary" size="medium" onClick={onCancel}>
                {formatMessage(templateEditorMessages.cancel)}
              </Button>
            )}
            <Button
              variant="primary"
              size="medium"
              disabled={!isFileMode && (!content.trim() || isParsing)}
              loading={isParsing}
              onClick={handleSubmit}
            >
              {formatMessage(modeConfig.action)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
